"use client";

import { storyblokInit } from "@storyblok/react/rsc";
import { ReactElement } from "react";

// General
import Page from "./Page";
import Contact from "./Contact";
import Signup from "./Signup";
import Button from "./Button";
import ButtonGroup from "./ButtonGroup";
import Cta from "./Cta";
import Hero from "./Hero";
import InfoHero from "./InfoHero";
import HeaderImage from "./HeaderImage";
import HeaderVideo from "./HeaderVideo";
import Grid from "./Grid";
import Table from "./Table";
import TableOfContents from "./TableOfContents";
import TableOfContentsItem from "./TableOfContentsItem";
import Slider from "./Slider";
import Tabs from "./Tabs";
import AssetLink from "./AssetLink";
import FileLink from "./FileLink";

// Landingpage
import FeatureSection from "./FeatureSection";
import FeatureListCard from "./FeatureListCard";
import FeatureListText from "./FeatureListText";
import FeatureList from "./FeatureList";
import FeatureItem from "./FeatureItem";
import TopicSection from "./TopicSection";
import ResultSection from "./ResultSection";
import ListSection from "./ListSection";
import ContentSection from "./ContentSection";
import InfoCard from "./InfoCard";
import InfoCardSection from "./InfoCardSection";
import IconListItem from "./IconListItem";
import PricingSection from "./PricingSection";
import DisclaimerListItem from "./DisclaimerListItem";
import TextSection from "./TextSection";
import TextBlock from "./TextBlock";
import TextBlockItem from "./TextBlockItem";
import ExpertDetail from "./ExpertDetail";
import ExpertMiniGallery from "./ExpertMiniGallery";
import ExpertMiniCard from "./ExpertMiniCard";
import ArticleGallery from "./ArticleGallery";
import EventGallery from "./EventGallery";
import EventListSection from "./EventListSection";
import Job from "./Job";
import JobListing from "./JobListing";
import TextContent from "./TextContent";
import TextImage from "./TextImage";
import TextVideo from "./TextVideo";
import TextCard from "./TextCard";
import ContentCard from "./ContentCard";
import ImageCard from "./ImageCard";
import ImageTeaser from "./ImageTeaser";
import NewsletterSection from "./NewsletterSection";
import EventSection from "./EventSection";
import HeadlineSection from "./HeadlineSection";
import ShapeGallery from "./ShapeGallery";
import ShapeGalleryItem from "./ShapeGalleryItem";
import Event from "./Event";
import EventInfoItem from "./EventInfoItem";
import EventSignUpSection from "./EventSignUpSection";
import InfoSection from "./InfoSection";
import InfoList from "./InfoList";
import InfoListItem from "./InfoListItem";
import InfoText from "./InfoText";
import InfoIconListItem from "./InfoIconListItem";
import StepsSection from "./StepsSection";
import StepsItem from "./StepsItem";
import CtaSection from "./CtaSection";
import Expert from "./Expert";
import ExpertGallery from "./ExpertGallery";
import HeadlineBlock from "./HeadlineBlock";
import QuoteGallery from "./QuoteGallery";
import QuoteCard from "./QuoteCard";
import NewsletterCta from "./NewsletterCta";
import ImageGallery from "./ImageGallery";
import VideoShortsGallery from "./VideoShortsGallery";
import TestimonialGallery from "./TestimonialGallery";
import SocialTrust from "./SocialTrust";
import TestimonialCard from "./TestimonialCard";
import PraxisTestimonialCard from "./PraxisTestimonialCard";
import EventPromoCard from "./EventPromoCard";
import PromoCard from "./PromoCard";
import SingleEventCard from "./SingleEventCard";
import SingleArticleCard from "./SingleArticleCard";
import TopicGallery from "./TopicGallery";
import TopicCard from "./TopicCard";
import TopicSummaryGallery from "./TopicSummaryGallery";
import TopicSummaryCard from "./TopicSummaryCard";
import SignupPromo from "./SignupPromo";
import InstagramLinktree from "./InstagramLinktree";
import InstagramLinkItem from "./InstagramLinkItem";

// Marketing
import Testimonial from "./Testimonial";
import TestimonialSection from "./TestimonialSection";
import Blog from "./Blog";
import Article from "./Article";
import Interviews from "./Interviews";
import Interview from "./Interview";
import FaqSection from "./FaqSection";
import LogoList from "./LogoList";
import Partner from "./Partner";
import ProductHub from "./ProductHub";
import Press from "./Press";
import PressRelease from "./PressRelease";
import PressSection from "./PressSection";
import Ad from "./Ad";
import AdSpace from "./AdSpace";

// Course
import Library from "./Library";
import Course from "./Course";
import Lesson from "./Lesson";
import Evaluation from "./Evaluation";
import Video from "./Video";
import VideoCollection from "./VideoCollection";
import VideoEmbed from "./VideoEmbed";
import VideoSection from "./VideoSection";
import ImageSection from "./ImageSection";

// Layout
import FallbackComponent from "./FallbackComponent";

// Bewusst OHNE `use: [apiPlugin]` und ohne accessToken: hier wird nur die
// Komponenten-Map fuer StoryblokComponent registriert.
//
// Seit die Galerien ueber /api/storyblok/list laufen, ruft keine
// Client-Komponente mehr die Storyblok-API auf — das Token gehoert damit
// nicht mehr ins Browser-Bundle. apiPlugin blieb beim Entfernen des Tokens
// aber stehen und beschwerte sich seitdem auf JEDEM Seitenaufruf in der
// Konsole ueber genau das Token, das er nicht mehr braucht:
//
//   "You need to provide an access token to interact with Storyblok API"
//
// Technisch folgenlos (apiPlugin loggt und gibt undefined zurueck, es wirft
// nicht), praktisch schaedlich: Der Fehler stand bei der Suche nach dem
// Staging-Ausfall ganz oben in der Konsole und wurde dadurch als Ursache
// gelesen, obwohl er keine war.
//
// StoryblokStory kommt ohne den Plugin aus: es nutzt useStoryblokState,
// das nur die Bridge registriert und getStoryblokApi nie aufruft
// (@storyblok/react/dist/client.mjs — null Treffer).
//
// Serverseitig ist der cachende Client davon unberuehrt, der wird in
// lib/storyblok/api.ts via initStoryblok() registriert.
//
// WARUM sich die beiden Inits nicht gegenseitig zerstoeren: storyblokInit
// setzt BEIDE Modulvariablen bedingungslos — den API-Client und die
// Komponenten-Map. Dieser Init hier wuerde den Client also nullen, der in
// api.ts wuerde die Map auf undefined setzen. Dass das folgenlos bleibt,
// liegt allein daran, dass Next Client- und Server-Modulgraph getrennt
// instanziiert: die Map lebt im Client-Graph, initStoryblok() im
// Server-Graph.
//
// ACHTUNG: Diese Trennung ist die einzige Absicherung. Wer den Provider-Init
// in eine Server-Komponente zieht oder StoryblokComponent serverseitig
// rendert, bringt beide in denselben Graph — dann ueberschreibt der letzte
// Init den jeweils anderen Teil, und getComponent wirft einen TypeError
// statt sauber `false` zu liefern.
storyblokInit({
  components: {
    // General
    page: Page,
    contact: Contact,
    signup: Signup,
    button: Button,
    button_group: ButtonGroup,
    cta: Cta,
    hero: Hero,
    info_hero: InfoHero,
    header_image: HeaderImage,
    header_video: HeaderVideo,
    grid: Grid,
    table: Table,
    table_of_contents: TableOfContents,
    table_of_contents_item: TableOfContentsItem,
    slider: Slider,
    tabs: Tabs,
    asset_link: AssetLink,
    file_link: FileLink,

    // Landingpage
    feature_section_new: FeatureSection,
    feature_list_card: FeatureListCard,
    feature_list_text: FeatureListText,
    feature_list: FeatureList,
    feature_item: FeatureItem,
    topic_section: TopicSection,
    result_section: ResultSection,
    list_section: ListSection,
    content_section: ContentSection,
    info_card: InfoCard,
    info_card_section: InfoCardSection,
    icon_list_item: IconListItem,
    pricing_section: PricingSection,
    disclaimer_list_item: DisclaimerListItem,
    text_section: TextSection,
    text_block: TextBlock,
    text_block_item: TextBlockItem,
    expert_detail: ExpertDetail,
    expert_mini_gallery: ExpertMiniGallery,
    expert_mini_card: ExpertMiniCard,
    article_gallery: ArticleGallery,
    event_gallery: EventGallery,
    event_list_section: EventListSection,
    job: Job,
    job_listing: JobListing,
    text_content: TextContent,
    text_image: TextImage,
    text_video: TextVideo,
    text_card: TextCard,
    content_card: ContentCard,
    image_card: ImageCard,
    image_teaser: ImageTeaser,
    newsletter_section: NewsletterSection,
    event_section: EventSection,
    event: Event,
    // Reisen teilen sich den Renderpfad mit Events — die Unterschiede
    // (kein Serientermin, keine Anmeldung) prueft Event.tsx ueber
    // blok.component. Fehlt dieser Eintrag, zeigt die Seite den englischen
    // Fallback-Text statt der Reise.
    travel: Event,
    event_info_item: EventInfoItem,
    event_signup_section: EventSignUpSection,
    headline_section: HeadlineSection,
    shape_gallery: ShapeGallery,
    shape_gallery_item: ShapeGalleryItem,
    info_section: InfoSection,
    info_list: InfoList,
    info_list_item: InfoListItem,
    info_text: InfoText,
    info_icon_list_item: InfoIconListItem,
    steps_section: StepsSection,
    steps_item: StepsItem,
    cta_section: CtaSection,
    expert: Expert,
    expert_gallery: ExpertGallery,
    headline_block: HeadlineBlock,
    quote_gallery: QuoteGallery,
    quote_card: QuoteCard,
    newsletter_cta: NewsletterCta,
    image_gallery: ImageGallery,
    video_shorts_gallery: VideoShortsGallery,
    testimonial_gallery: TestimonialGallery,
    social_trust: SocialTrust,
    testimonial_card: TestimonialCard,
    praxis_testimonial_card: PraxisTestimonialCard,
    event_promo_card: EventPromoCard,
    promo_card: PromoCard,
    single_event_card: SingleEventCard,
    single_article_card: SingleArticleCard,
    topic_gallery: TopicGallery,
    topic_card: TopicCard,
    topic_summary_gallery: TopicSummaryGallery,
    topic_summary_card: TopicSummaryCard,
    signup_promo: SignupPromo,
    instagram_linktree: InstagramLinktree,
    instagram_link_item: InstagramLinkItem,
    // Marketing
    blog: Blog,
    article: Article,
    interviews: Interviews,
    interview: Interview,
    testimonial: Testimonial,
    testimonial_section: TestimonialSection,
    faq_section: FaqSection,
    logo_list: LogoList,
    partner: Partner,
    product_hub: ProductHub,
    press: Press,
    press_release: PressRelease,
    press_section: PressSection,
    ad: Ad,
    ad_space: AdSpace,
    // Course
    library: Library,
    course: Course,
    lesson: Lesson,
    evaluation: Evaluation,
    video: Video,
    video_collection: VideoCollection,
    video_embed: VideoEmbed,
    video_section: VideoSection,
    image_section: ImageSection,
  },
  enableFallbackComponent: true,
  customFallbackComponent: FallbackComponent,
});

type StoryblokProviderProps = {
  children: ReactElement;
};

export default function StoryblokProvider({
  children,
}: StoryblokProviderProps) {
  return children;
}
